import {useFormikContext} from 'formik';
import {
    Alert,
    Select,
    Stack,
} from '@mantine/core';
import {AiOutlineWarning} from 'react-icons/ai';

import {useApplicationOptions} from '../hooks/useApplicationOptions';

type ApplicationFieldProps = {
    // The Formik field name — `applicationId` in both testing catalogs.
    name?: string,
    label?: string,
    description?: string,
};

// FNP-979. Picks the console-api `Application` a testing catalog row targets.
//
// Replaces a free-text "Target Application" TextInput: `targetApplication:
// String!` was a hand-typed label that nothing read and that let two rows spell
// the same application differently and never join. This binds the real foreign
// key instead.
//
// ⚠️ NO REQUIRED-FIELD GUARD, deliberately. `applicationId` is nullable on the
// API and is NOT validated at write time (FNP-859: validate at execution, never
// at write) — a dangling id is legal because an application can be archived
// after a row references it. Do not add `withAsterisk` or a Yup `.required()`.
export const ApplicationField = ({
                                     name = 'applicationId',
                                     label = 'Target Application',
                                     description = 'The application this targets. Resolved from the console application catalog.',
                                 }: ApplicationFieldProps) => {

    const {
        values,
        setFieldValue,
        errors,
    } = useFormikContext<Record<string, unknown>>();

    const rawValue = values[name];
    const selectedId = typeof rawValue === 'string' && rawValue ? rawValue : null;

    const {
        options,
        selectedOption,
        isLoading,
        isError,
    } = useApplicationOptions(selectedId);

    const rawError = errors[name];
    const fieldError = typeof rawError === 'string' ? rawError : undefined;

    return (
        <Stack gap={'sm'} my={'xl'}>
            <Select
                aria-label={label}
                label={label}
                description={description}
                placeholder={isLoading ? 'Loading applications…' : 'Select an application'}
                data={options.map(({
                                       value,
                                       label: optionLabel,
                                   }) => ({
                    value,
                    label: optionLabel,
                }))}
                value={selectedId}
                // Clearing yields null, which is exactly what the API wants for
                // "no application" — `applicationId: ID` is nullable.
                onChange={(value) => setFieldValue(name, value ?? '')}
                disabled={isLoading}
                searchable
                clearable
                error={fieldError}
            />

            {isError ? (
                <Alert color='yellow' variant='light' icon={<AiOutlineWarning/>}>
                    The application catalog could not be loaded, so the list is
                    empty. Any application already selected on this record is
                    preserved and will not be changed by saving.
                </Alert>
            ) : null}

            {/* Authoring-time surface for what the executor would hit at run
                time: it fails OPEN with APPLICATION_URL_MISSING /
                APPLICATION_URL_UNREACHABLE rather than erroring, so without this
                the run just quietly does nothing useful. Warn, never block. */}
            {selectedOption?.unnavigable ? (
                <Alert
                    color='yellow'
                    variant='light'
                    icon={<AiOutlineWarning/>}
                    title='This application is not navigable'
                >
                    {selectedOption.unnavigableReason === 'application not found'
                        ? 'This record points at an application that is no longer in the catalog. That is allowed — the reference is kept as-is — but a test run against it cannot navigate anywhere.'
                        : `The selected application has ${selectedOption.unnavigableReason}, so the executor cannot open it. The run will record APPLICATION_URL_MISSING or APPLICATION_URL_UNREACHABLE instead of exercising the app. You can still save this.`}
                </Alert>
            ) : null}
        </Stack>
    );
};
